// src/whatsappChat/components/TemplateTestModal.jsx
import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { useWaSendTemplateTest, useWaTemplateTest } from '../api/templateHooks';
import { normalizeError, sendErrorMessage } from '../api/client';
import { digitsOnly, fillTemplate, isHttpsUrl, templateHeader } from '../api/content';
import { Field, Modal } from './Modal';
import { buttonLabel, TemplatePreview, TemplateStatus } from './TemplatePreview';
import { showToast } from '../../components/toast.jsx';

/** Sends one approved template to a single number as a test. */
export function TemplateTestModal({ row, numberId, onClose }) {
  const { data, isLoading, isError, error } = useWaTemplateTest(row.id, numberId);
  const send = useWaSendTemplateTest();

  const [phone, setPhone] = useState('');
  const [bodyValues, setBodyValues] = useState([]);
  const [buttonValues, setButtonValues] = useState([]);
  const [headerLink, setHeaderLink] = useState('');
  const [customHeader, setCustomHeader] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [sendError, setSendError] = useState('');

  const tpl = data?.template || row;
  const bodyFields = data?.fields?.body_params || [];
  const buttonFields = data?.fields?.button_params || [];
  const headerField = data?.fields?.header || null;
  const headerRequired = Boolean(headerField?.required);
  const askHeader = Boolean(headerField) && (headerRequired || customHeader);

  useEffect(() => {
    if (!data) return;
    setBodyValues((data.fields?.body_params || []).map((f) => f.example || ''));
    setButtonValues((data.fields?.button_params || []).map((f) => f.example || ''));
  }, [data]);

  const errors = {};
  if (digitsOnly(phone).length < 10) errors.phone = 'Enter a phone number with at least 10 digits';
  bodyFields.forEach((f, i) => {
    if (!String(bodyValues[i] || '').trim()) errors[`body-${i}`] = 'Required';
  });
  buttonFields.forEach((f, i) => {
    if (!String(buttonValues[i] || '').trim()) errors[`button-${i}`] = 'Required';
  });
  if (askHeader && (headerRequired || headerLink.trim()) && !isHttpsUrl(headerLink)) {
    errors.header = 'Enter a public https link';
  }
  const err = (key) => (showErrors ? errors[key] : null);

  const canSend = Boolean(data?.can_send);
  const sending = send.isPending;

  const submit = async (e) => {
    e.preventDefault();
    if (sending || !canSend) return;
    if (Object.keys(errors).length > 0) {
      setShowErrors(true);
      return;
    }
    setSendError('');
    const link = askHeader ? headerLink.trim() : '';
    try {
      await send.mutateAsync({
        id: row.id,
        ...(data.number_id || numberId ? { number_id: data.number_id || numberId } : {}),
        phone: phone.trim(),
        ...(bodyFields.length ? { body_params: bodyValues.map((v) => String(v).trim()) } : {}),
        ...(buttonFields.length ? { button_params: buttonValues.map((v) => String(v).trim()) } : {}),
        ...(link ? { header_link: link, header_type: headerField.type || 'image' } : {}),
      });
      showToast.success(`Test message sent to ${phone.trim()}.`);
      onClose();
    } catch (sendErr) {
      setSendError(sendErrorMessage(sendErr));
    }
  };

  // Body values sit in field order; the preview needs them by {{n}}.
  const previewVars = [];
  bodyFields.forEach((f, i) => {
    previewVars[(Number(f.index) || i + 1) - 1] = bodyValues[i];
  });
  const header = templateHeader(tpl);
  const previewLink = askHeader && isHttpsUrl(headerLink) ? headerLink.trim() : headerField?.default_link;
  const setAt = (setter, index, value) => setter((prev) => prev.map((v, i) => (i === index ? value : v)));

  return (
    <Modal
      title={`Send test · ${tpl.name}`}
      badge={<TemplateStatus status={tpl.status} />}
      onClose={onClose}
      locked={sending}
    >
      {isLoading ? (
        <div className="wa-state">Loading template…</div>
      ) : isError ? (
        <div className="wa-state">{normalizeError(error).message}</div>
      ) : (
        <form className="wa-form" onSubmit={submit} noValidate>
          {!canSend && (
            <div className="wa-alert wa-alert-danger wa-alert-flush">
              {data?.reason || 'This template cannot be sent as a test.'}
            </div>
          )}

          <TemplatePreview
            headerType={header.type || String(headerField?.type || '').toUpperCase()}
            header={header.text}
            mediaUrl={previewLink || header.mediaUrl}
            body={fillTemplate(tpl.body, previewVars)}
            footer={tpl.footer}
            buttons={(Array.isArray(tpl.buttons) ? tpl.buttons : []).map(buttonLabel)}
          />

          <Field label="Phone number" hint="10 digits are treated as an Indian number" error={err('phone')}>
            <input
              className="wa-input"
              type="tel"
              inputMode="tel"
              autoFocus
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="9876543210"
              disabled={!canSend || sending}
            />
          </Field>

          {bodyFields.map((f, i) => (
            <Field key={`body-${i}`} label={`Variable ${f.variable || `{{${i + 1}}}`}`} error={err(`body-${i}`)}>
              <input
                className="wa-input"
                type="text"
                value={bodyValues[i] || ''}
                onChange={(e) => setAt(setBodyValues, i, e.target.value)}
                disabled={!canSend || sending}
              />
            </Field>
          ))}

          {buttonFields.map((f, i) => (
            <Field
              key={`button-${i}`}
              label={f.label || `Button ${i + 1}`}
              hint={f.url ? `Only the part that replaces {{1}} in ${f.url}` : null}
              error={err(`button-${i}`)}
            >
              <input
                className="wa-input"
                type="text"
                value={buttonValues[i] || ''}
                onChange={(e) => setAt(setButtonValues, i, e.target.value)}
                disabled={!canSend || sending}
              />
            </Field>
          ))}

          {headerField && !headerRequired && (
            <label className="wa-check">
              <input
                type="checkbox"
                checked={customHeader}
                onChange={(e) => setCustomHeader(e.target.checked)}
                disabled={!canSend || sending}
              />
              <span>Use a different header {headerField.type || 'image'}</span>
            </label>
          )}

          {askHeader && (
            <Field
              label={`Header ${headerField.type || 'image'} link`}
              hint="Public https link to the file"
              error={err('header')}
            >
              <input
                className="wa-input"
                type="url"
                value={headerLink}
                onChange={(e) => setHeaderLink(e.target.value)}
                placeholder="https://"
                disabled={!canSend || sending}
              />
            </Field>
          )}

          {sendError && <div className="wa-alert wa-alert-danger wa-alert-flush">{sendError}</div>}

          <div className="wa-modal-actions">
            <button type="button" className="wa-btn wa-btn-secondary" onClick={onClose} disabled={sending}>
              Cancel
            </button>
            <button type="submit" className="wa-btn wa-btn-primary" disabled={!canSend || sending}>
              <FontAwesomeIcon icon={faPaperPlane} /> {sending ? 'Sending…' : 'Send test'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
